import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageConstants, PaymentErrorConstants } from '@uzurv/rideWss/app/constants';
import { ServiceException } from '@uzurv/rideWss/app/exceptions';
import { LoadingDialogService, ModalService } from '@uzurv/rideWss/app/services';
import { AuthenticatedRiderService } from '@uzurv/rideWss/app/services/authenticated-rider/authenticated-rider.service';
import {
  CardTypeConstants,
  CollectionRequest,
  PaymentAccount,
  PaymentAccountErrorConstants,
  PaymentAccountService,
  PaymentMethodConstants,
  PaymentStatusConstants,
  RiderService
} from '@uzurv/rideWss/clara';
import { cloneDeep } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as PaymentActions from './payment.actions';
import { getCardTypes, getEarliestChargeOrder, getPaymentMethods, paymentState } from './payment.selectors';
import { PaymentState } from './payment.state';

@Injectable()
export class PaymentEffects {
  constructor(
    private actions$: Actions,
    private authenticatedRiderService: AuthenticatedRiderService,
    private loadingDialogService: LoadingDialogService,
    private modalService: ModalService,
    private paymentAccountService: PaymentAccountService,
    private riderService: RiderService,
    private store$: Store<State<PaymentState>>,
    private translateService: TranslateService
  ) {}

  fetchPaymentAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.fetchPaymentAccounts, PaymentActions.refreshPaymentAccounts),
      switchMap(_ => {
        return this.riderService.listAllPaymentAccountsByRiderId(this.authenticatedRiderService.getLoggedInRiderId()).pipe(
          map(paymentAccounts => {
            void this.loadingDialogService.dismissLoadingDialog();
            return PaymentActions.fetchPaymentAccountsSuccess({
              paymentAccounts: this.paymentAccountService.sortPaymentAccountsByChargeOrder(paymentAccounts)
            });
          }),
          catchError(err => of(PaymentActions.fetchPaymentAccountsFail({ error: err })))
        );
      })
    );
  });

  fetchPaymentMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.fetchPaymentMethods),
      switchMap(_ =>
        this.paymentAccountService.getPaymentMethods().pipe(
          map(paymentMethods => PaymentActions.fetchPaymentMethodsSuccess({ paymentMethods: paymentMethods })),
          catchError(error => of(PaymentActions.fetchPaymentMethodsFail({ error: error })))
        )
      )
    );
  });

  fetchLastUsedPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.fetchPaymentAccountsSuccess),
      map(action => {
        const lastUsedPaymentAccountId = localStorage.getItem(LocalStorageConstants.RIDER_LAST_USED_PAYMENT_ACCOUNT_ID_KEY);
        const lastUsedPaymentAccount = lastUsedPaymentAccountId
          ? action.paymentAccounts.find(paymentAccount => paymentAccount.id === +lastUsedPaymentAccountId)
          : null;
        if (lastUsedPaymentAccount && lastUsedPaymentAccount.active) {
          return PaymentActions.fetchLastUsedPaymentAccountSuccess({ paymentAccount: lastUsedPaymentAccount });
        }

        return PaymentActions.fetchLastUsedPaymentAccountFail();
      })
    );
  });

  setLastUsedPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.setLastUsedPaymentAccount),
      map(action => {
        if (action.paymentAccount && action.paymentAccount.id) {
          localStorage.setItem(LocalStorageConstants.RIDER_LAST_USED_PAYMENT_ACCOUNT_ID_KEY, action.paymentAccount.id.toString());
          return PaymentActions.fetchLastUsedPaymentAccountSuccess({ paymentAccount: action.paymentAccount });
        }

        localStorage.removeItem(LocalStorageConstants.RIDER_LAST_USED_PAYMENT_ACCOUNT_ID_KEY);
        return PaymentActions.fetchLastUsedPaymentAccountFail();
      })
    );
  });

  fetchCardTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.fetchCardTypes),
      switchMap(_ =>
        this.paymentAccountService.getCardTypes().pipe(
          map(cardTypes => PaymentActions.fetchCardTypesSuccess({ cardTypes: cardTypes })),
          catchError(error => of(PaymentActions.fetchCardTypesFail({ error: error })))
        )
      )
    );
  });

  addPaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.addPaymentAccount),
      withLatestFrom(this.store$.select(getEarliestChargeOrder), this.store$.select(getCardTypes), this.store$.select(getPaymentMethods)),
      switchMap(([action, earliestChargeOrder, cardTypes, paymentMethods]) => {
        const paymentAccount: PaymentAccount = cloneDeep(action.paymentAccount);
        paymentAccount.personId = this.authenticatedRiderService.getLoggedInRiderId();
        paymentAccount.cardType = cardTypes.find(cardType => {
          const cardTypeCodeFromCardTypeString = this.getCardTypeCodeFromCardTypeString(action.cardTypeString);
          return cardType.code === cardTypeCodeFromCardTypeString;
        });

        paymentAccount.paymentMethod = paymentMethods.find(paymentMethod => paymentMethod.code === PaymentMethodConstants.CARD);
        const cardExpirationValue: string = paymentAccount.cardExpiration;
        paymentAccount.cardExpiration = `${cardExpirationValue.split('/')[1]}-${cardExpirationValue.split('/')[0]}`.trim();
        paymentAccount.chargeOrder = earliestChargeOrder - 1;

        void this.loadingDialogService.presentLoadingDialog(
          this.translateService.instant('paymentAccountList.loading.addingPaymentAccount')
        );
        return this.riderService.createPaymentAccount(this.authenticatedRiderService.getLoggedInRiderId(), paymentAccount).pipe(
          map(paymentAccount => {
            this.store$.dispatch(PaymentActions.fetchPaymentAccounts());
            return PaymentActions.addPaymentAccountSuccess({ paymentAccount: paymentAccount });
          }),
          catchError(error => of(PaymentActions.addPaymentAccountFail({ error: error })))
        );
      })
    );
  });

  updatePaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.updatePaymentAccount),
      withLatestFrom(this.store$.select(getPaymentMethods)),
      switchMap(([action, paymentMethods]) => {
        const paymentAccount: PaymentAccount = cloneDeep(action.paymentAccount);
        const cardExpirationValue: string = action.paymentAccount.cardExpiration;
        if (paymentAccount.cardExpiration.includes('/')) {
          paymentAccount.cardExpiration = `${cardExpirationValue.split('/')[1]}-${cardExpirationValue.split('/')[0]}`.trim();
        }
        paymentAccount.paymentMethod = paymentMethods.find(paymentMethod => paymentMethod.code === PaymentMethodConstants.CARD);

        void this.loadingDialogService.presentLoadingDialog(
          this.translateService.instant('paymentAccountList.loading.updatingPaymentAccount')
        );
        return this.riderService.updatePaymentAccount(this.authenticatedRiderService.getLoggedInRiderId(), paymentAccount).pipe(
          map(paymentAccount => {
            this.store$.dispatch(PaymentActions.fetchPaymentAccounts());
            void this.loadingDialogService.dismissLoadingDialog();
            return PaymentActions.updatePaymentAccountSuccess({ paymentAccount: paymentAccount });
          }),
          catchError(error => of(PaymentActions.updatePaymentAccountFail({ error: error })))
        );
      })
    );
  });

  deletePaymentAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.deletePaymentAccount),
      withLatestFrom(this.store$.select(paymentState)),
      switchMap(([action, _]) => {
        void this.loadingDialogService.presentLoadingDialog(
          this.translateService.instant('paymentAccountList.loading.deletingPaymentAccount')
        );

        return this.riderService.deletePaymentAccount(this.authenticatedRiderService.getLoggedInRiderId(), action.paymentAccount.id).pipe(
          map(() => {
            this.store$.dispatch(PaymentActions.fetchPaymentAccounts());
            return PaymentActions.deletePaymentAccountSuccess({ paymentAccount: action.paymentAccount });
          }),
          catchError(error => of(PaymentActions.deletePaymentAccountFail({ error: error })))
        );
      })
    );
  });

  deletePaymentAccountError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PaymentActions.deletePaymentAccountFail),
        tap(action => {
          void this.loadingDialogService.dismissLoadingDialog();
          if (PaymentAccountErrorConstants.LAST_PAYMENT_ACCOUNT_CANNOT_BE_DELETED.test(action.error.message)) {
            void this.modalService.createNgrxErrorModalDialog(
              this.translateService.instant('paymentAccount.error.message.deleteLastPaymentAccount')
            );
          } else {
            void this.modalService.presentAlertDialog({
              icon: 'uz-alert-circle-outline',
              title: this.translateService.instant('deleteAccount.error.generic.title'),
              htmlContent: this.translateService.instant('deleteAccount.error.generic.message'),
              buttons: [
                {
                  text: this.translateService.instant('button.ok'),
                  role: 'cancel'
                }
              ]
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  makePaymentAccountDefault$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.makePaymentAccountDefault),
      withLatestFrom(this.store$.select(getEarliestChargeOrder)),
      map(([action, earliestChargeOrder]) => {
        const paymentAccount: PaymentAccount = cloneDeep(action.paymentAccount);
        paymentAccount.chargeOrder = earliestChargeOrder - 1;
        return PaymentActions.updatePaymentAccount({ paymentAccount: paymentAccount });
      })
    );
  });

  getOutstandingCharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.fetchOutstandingCharges, PaymentActions.submitPaymentForOutstandingChargesSuccess),
      switchMap(_ => {
        return this.riderService.listAllCharges(this.authenticatedRiderService.getLoggedInRiderId(), true).pipe(
          map(outstandingCharges => PaymentActions.fetchOutstandingChargesSuccess({ charges: outstandingCharges })),
          catchError(error => of(PaymentActions.fetchOutstandingChargesFail({ error: error })))
        );
      })
    );
  });

  submitPaymentForOutstandingCharges$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PaymentActions.submitPaymentForOutstandingCharges),
      switchMap(action => {
        const collectionRequest = new CollectionRequest();
        collectionRequest.paymentAccountId = action.paymentAccountId;
        collectionRequest.chargeIds = action.charges.map(charge => charge.id);

        return this.riderService.collectPaymentForCharges(action.riderId, collectionRequest).pipe(
          map(completedPayment => {
            if (completedPayment.paymentStatus.name === PaymentStatusConstants.PROCESSING_FAILED) {
              return PaymentActions.submitPaymentForOutstandingChargesFail({ error: new ServiceException('Processing Payment Failed') });
            } else {
              return PaymentActions.submitPaymentForOutstandingChargesSuccess({ payment: completedPayment });
            }
          }),
          catchError(error => of(PaymentActions.submitPaymentForOutstandingChargesFail({ error: error })))
        );
      })
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PaymentActions.addPaymentAccountFail, PaymentActions.updatePaymentAccountFail),
        map(() => {
          void this.loadingDialogService.dismissLoadingDialog();
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Return the CardTypeConstant that corresponds to the cardTypeString returned from the CreditCard library.
   * @param cardTypeString
   * @private
   */
  private getCardTypeCodeFromCardTypeString(cardTypeString: string): string {
    switch (cardTypeString) {
      case 'amex': {
        return CardTypeConstants.CARD_TYPE_CODE_AMEX;
      }
      case 'dinersclub': {
        return CardTypeConstants.CARD_TYPE_CODE_DINERS_CLUB;
      }
      case 'discover': {
        return CardTypeConstants.CARD_TYPE_CODE_DISCOVER;
      }
      case 'visa': {
        return CardTypeConstants.CARD_TYPE_CODE_VISA;
      }
      case 'mastercard': {
        return CardTypeConstants.CARD_TYPE_CODE_MASTER_CARD;
      }
      case 'jcb': {
        return CardTypeConstants.CARD_TYPE_CODE_JCB;
      }
      default: {
        return null;
      }
    }
  }
}
